/**
 * EVENTSFLO-ADMIN-BACKUP-REMINDER-SETTINGS-V1
 *
 * Read-only display card with an Edit dialog for the SuperAdmin
 * reminder settings. NO scheduler, NO email delivery, NO automated
 * backup creation — V1 surfaces a dashboard warning when reminders
 * are enabled and the most recent retrievable archive is older than
 * the configured frequency window.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, BellRing, Pencil } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { getReminderSettings, updateReminderSettings } from "./backupApi";
import { formatDate } from "./backupFormatters";

const FREQUENCIES = ["daily", "weekly", "off"];
const DAYS = [
  "monday", "tuesday", "wednesday", "thursday",
  "friday", "saturday", "sunday",
];

export default function BackupReminderSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getReminderSettings();
      setData(data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load reminder settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  if (loading || !data) {
    return (
      <Card data-testid="reminder-settings-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BellRing className="w-4 h-4" /> Backup reminder settings
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-zinc-400">Loading…</CardContent>
      </Card>
    );
  }

  const s = data.settings;
  const showOverdue = data.is_overdue && s.dashboard_warning_enabled;

  return (
    <>
      <Card data-testid="reminder-settings-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <BellRing className="w-4 h-4" /> Backup reminder settings
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditOpen(true)}
            data-testid="reminder-edit-btn"
          >
            <Pencil className="w-3 h-3 mr-1" /> Edit
          </Button>
        </CardHeader>
        <CardContent>
          {showOverdue ? (
            <div
              className="mb-3 flex items-start gap-2 rounded border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-300"
              data-testid="reminder-overdue-banner"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <div>
                <div className="font-semibold">Backup overdue</div>
                The most recent retrievable backup is older than your configured{" "}
                <strong>{s.frequency}</strong> window. Create a new backup to clear this
                warning.
              </div>
            </div>
          ) : null}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-zinc-400">Reminders</div>
              <div className="font-medium">{s.enabled ? "Enabled" : "Disabled"}</div>
            </div>
            <div>
              <div className="text-zinc-400">Frequency</div>
              <div className="font-medium">{s.frequency}</div>
            </div>
            <div>
              <div className="text-zinc-400">Window</div>
              <div className="font-medium">
                {s.frequency === "weekly" ? `${s.preferred_day} ` : ""}
                {s.preferred_time}
                {" "}
                <span className="text-zinc-500">({s.timezone})</span>
              </div>
            </div>
            <div>
              <div className="text-zinc-400">Recipients</div>
              <div className="font-medium">{s.recipients.length}</div>
            </div>
            <div>
              <div className="text-zinc-400">Last backup</div>
              <div className="font-medium">{formatDate(data.last_backup_at)}</div>
            </div>
            <div>
              <div className="text-zinc-400">Next due (est.)</div>
              <div className="font-medium">{formatDate(data.next_due_at_estimate)}</div>
            </div>
            <div>
              <div className="text-zinc-400">Dashboard warning</div>
              <div className="font-medium">
                {s.dashboard_warning_enabled ? "On" : "Off"}
              </div>
            </div>
            <div>
              <div className="text-zinc-400">Email reminders</div>
              <div className="text-zinc-300 text-xs">Not yet implemented</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <ReminderEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        initial={s}
        onSaved={async () => {
          setEditOpen(false);
          await fetchSettings();
        }}
      />
    </>
  );
}

function ReminderEditDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial);
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const recipients = (form.recipients_csv ?? form.recipients.join(", "))
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
      const body = {
        enabled: !!form.enabled,
        frequency: form.frequency,
        preferred_day: form.preferred_day,
        preferred_time: form.preferred_time,
        timezone: form.timezone,
        recipients,
        dashboard_warning_enabled: !!form.dashboard_warning_enabled,
      };
      await updateReminderSettings(body);
      toast.success("Reminder settings saved");
      await onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        (detail && (detail.message || detail.error)) ||
        e?.message ||
        "Save failed";
      toast.error(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="reminder-settings-dialog">
        <DialogHeader>
          <DialogTitle>Edit backup reminder settings</DialogTitle>
          <DialogDescription>
            V1 surfaces a dashboard warning only. Automated backup generation,
            offsite backup, and email delivery remain future phases.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2 flex items-center justify-between">
            <Label htmlFor="reminder-enabled">Enable reminders</Label>
            <Switch
              id="reminder-enabled"
              checked={!!form.enabled}
              onCheckedChange={(v) => set("enabled", !!v)}
              data-testid="reminder-enabled-switch"
            />
          </div>

          <div className="space-y-1">
            <Label>Frequency</Label>
            <Select
              value={form.frequency}
              onValueChange={(v) => set("frequency", v)}
            >
              <SelectTrigger data-testid="reminder-frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCIES.map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Preferred day {form.frequency === "weekly" ? "" : "(weekly only)"}</Label>
            <Select
              value={form.preferred_day}
              onValueChange={(v) => set("preferred_day", v)}
              disabled={form.frequency !== "weekly"}
            >
              <SelectTrigger data-testid="reminder-preferred-day-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAYS.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="reminder-time">Preferred time (HH:MM)</Label>
            <Input
              id="reminder-time"
              value={form.preferred_time}
              onChange={(e) => set("preferred_time", e.target.value)}
              placeholder="09:00"
              data-testid="reminder-preferred-time-input"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="reminder-tz">Timezone</Label>
            <Input
              id="reminder-tz"
              value={form.timezone}
              onChange={(e) => set("timezone", e.target.value)}
              placeholder="Europe/Monaco"
              data-testid="reminder-timezone-input"
            />
          </div>

          <div className="col-span-2 space-y-1">
            <Label htmlFor="reminder-recipients">
              Recipients (comma-separated, max 10) — email delivery not yet active
            </Label>
            <Input
              id="reminder-recipients"
              value={form.recipients_csv ?? form.recipients.join(", ")}
              onChange={(e) => set("recipients_csv", e.target.value)}
              placeholder="ops@example.com, sre@example.com"
              data-testid="reminder-recipients-input"
            />
          </div>

          <div className="col-span-2 flex items-center justify-between">
            <Label htmlFor="reminder-dashboard">Show dashboard warning when overdue</Label>
            <Switch
              id="reminder-dashboard"
              checked={!!form.dashboard_warning_enabled}
              onCheckedChange={(v) => set("dashboard_warning_enabled", !!v)}
              data-testid="reminder-dashboard-warning-switch"
            />
          </div>

          <div className="col-span-2 flex items-center justify-between opacity-60">
            <Label>Send email reminders</Label>
            <Switch checked={false} disabled />
          </div>
          <div className="col-span-2 text-xs text-zinc-400 -mt-2">
            Email delivery not yet implemented
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="reminder-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="reminder-save-btn"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
